import { FC, useMemo, useState } from "react";
import {
  ArrowRightIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";

const contactHighlights = [
  {
    icon: MapPinIcon,
    title: "Visit Our Office",
    text: "Liwara 2, Ajman, United Arab Emirates",
  },
  {
    icon: PhoneIcon,
    title: "Call Our Team",
    text: "+971 6 7421322",
  },
  {
    icon: EnvelopeIcon,
    title: "Email Us",
    text: "info@bcrltduk.com",
  },
];

const SectionContactPreview: FC = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const inquiryLinkState = useMemo(
    () => ({
      name,
      email,
      message,
    }),
    [name, email, message]
  );

  return (
    <section className="relative py-14 sm:py-16 lg:py-24 bg-neutral-50 dark:bg-neutral-800/50 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-12 left-1/4 w-56 h-56 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-secondary2-500/10 blur-3xl" />
      </div>

      <div className="container relative px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-primary-600">
            Contact BCR
          </span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white">
            Let&apos;s Discuss Your Next Project
          </h2>
          <p className="mt-4 text-sm sm:text-base text-neutral-500 dark:text-neutral-400 leading-relaxed">
            Reach out for product recommendations, technical guidance, pricing,
            or a custom quotation for your upcoming project.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.02fr_0.98fr] gap-6 lg:gap-8 items-stretch">
          <div className="relative rounded-[1.75rem] overflow-hidden bg-[#09162c] text-white border border-white/10 shadow-[0_24px_70px_rgba(4,12,24,0.35)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.16),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(244,63,94,0.16),transparent_32%)]" />
            <div className="absolute inset-0 opacity-30 bcr-contact-grid" />

            <div className="relative p-6 sm:p-8 lg:p-10 h-full flex flex-col">
              <div className="max-w-lg">
                <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-primary-300">
                  Quick Response
                </span>
                <h3 className="mt-4 text-2xl sm:text-3xl font-bold leading-tight">
                  Speak with our specialists for faster project support.
                </h3>
                <p className="mt-4 text-sm sm:text-base text-white/65 leading-relaxed">
                  From expansion joint systems to tile trims and skirting
                  solutions, our team helps you choose the right profile for
                  your design, budget, and site conditions.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contactHighlights.slice(0, 2).map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-white/10 bg-white/6 backdrop-blur-sm p-4 sm:p-5 transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                        <item.icon className="w-5 h-5 text-primary-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">
                          {item.title}
                        </p>
                        <p className="mt-1 text-sm text-white/60 leading-relaxed">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-[1.5rem] border border-white/10 bg-gradient-to-r from-white/8 to-white/4 p-5 sm:p-6 backdrop-blur-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-white">
                      Need urgent assistance?
                    </p>
                    <p className="mt-1 text-sm text-white/60">
                      Call or message us for faster product and quote support.
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-2 self-start sm:self-auto rounded-full border border-white/15 bg-white/8 px-4 py-2 text-sm font-semibold text-white">
                    <PhoneIcon className="w-4 h-4 text-primary-300" />
                    +971 506 454 326
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-[1.5rem] border border-white/10 bg-white/6 p-5 sm:p-6 backdrop-blur-sm bcr-float-soft">
                  <p className="text-xs uppercase tracking-[0.25em] text-primary-300 font-semibold">
                    Product Support
                  </p>
                  <p className="mt-3 text-lg font-bold text-white">
                    Specification Guidance
                  </p>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">
                    Share your requirement and our team will suggest the right
                    BCR system for your project.
                  </p>
                </div>

                <div className="rounded-[1.5rem] border border-white/10 bg-white/6 p-5 sm:p-6 backdrop-blur-sm bcr-float-soft-delayed">
                  <p className="text-xs uppercase tracking-[0.25em] text-secondary2-300 font-semibold">
                    Commercial Inquiry
                  </p>
                  <p className="mt-3 text-lg font-bold text-white">
                    Quotation & Availability
                  </p>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">
                    Get pricing, stock information, and delivery support across
                    the UAE and GCC.
                  </p>
                </div>
              </div>

              <div className="mt-6 sm:mt-auto pt-2">
                <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-4 sm:p-5 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <EnvelopeIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      Email for drawings, BOQs, or catalog requests
                    </p>
                    <p className="mt-1 text-sm text-white/60">
                      info@bcrltduk.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form
            className="relative rounded-[1.75rem] bg-white dark:bg-neutral-900 border border-neutral-200/70 dark:border-neutral-700/70 shadow-bcr-card p-6 sm:p-8 lg:p-10"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="text-xs font-semibold tracking-[0.22em] uppercase text-primary-600">
                  Send Message
                </span>
                <h3 className="mt-2 text-2xl sm:text-[2rem] font-bold text-neutral-900 dark:text-white">
                  Start Your Inquiry
                </h3>
                <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-md">
                  Fill in the details below and continue to our contact page
                  with your message already prepared.
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-900/20 items-center justify-center">
                <ArrowRightIcon className="w-5 h-5 text-primary-600 dark:text-primary-300" />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Full Name
                </span>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  className="!rounded-2xl !bg-neutral-50 dark:!bg-neutral-800/70 !border-neutral-200 dark:!border-neutral-700"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Email Address
                </span>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  className="!rounded-2xl !bg-neutral-50 dark:!bg-neutral-800/70 !border-neutral-200 dark:!border-neutral-700"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
            </div>

            <label className="block mt-5">
              <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Message
              </span>
              <Textarea
                rows={6}
                placeholder="Tell us about your project, product requirement, or quotation request..."
                className="!rounded-[1.25rem] !bg-neutral-50 dark:!bg-neutral-800/70 !border-neutral-200 dark:!border-neutral-700 px-4 py-3"
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>

            <div className="mt-6 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50/80 dark:bg-neutral-800/50 p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                    What happens next?
                  </p>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    We&apos;ll take you to the full contact page so you can send
                    your inquiry with more details if needed.
                  </p>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-primary-600 dark:text-primary-300 font-semibold">
                  Fast Response
                </div>
              </div>
            </div>

            <Link to="/contact" state={inquiryLinkState} className="block mt-6">
              <ButtonPrimary className="w-full text-center !py-3.5 !rounded-full">
                Continue to Contact Page
              </ButtonPrimary>
            </Link>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SectionContactPreview;
